import { disableSharingAsPng } from 'magma/common/data';
import {
  faTrash, faCloudDownload, faShare, faFileExport, faUserPlus, faCopy, faPen, faCode, faLink, faFolderTree, trashArrowUpIcon,
} from 'magma/common/icons';
import { Permission, SvgIconDefinition } from 'magma/common/interfaces';
import { EntityData, EntityType, ProjectData, TeamRole } from 'shared/interfaces';
import { hasPermissionFlag } from 'shared/utils';


export interface EntityToolbarButton {
  id: string;
  title?: string;
  clickedTitle?: string;
  tooltip?: string;
  icon?: SvgIconDefinition;
  disabled?: boolean;
  clicked?: boolean;
  allowClick?: boolean;
}

export function getToolbar(entity: EntityData | EntityData[], roles: TeamRole[], project: ProjectData | undefined, isBinFolder = false): EntityToolbarButton[] {
  const DIVIDER = { id: '' };
  const READONLY = { disabled: true, tooltip: 'You have read-only access. Please request write access from team owner' };

  if (Array.isArray(entity)) {
    if (entity.length === 1) {
      entity = entity[0];
    } else {
      const entities = entity;
      const areEntitiesReadOnly = (flag: Permission) => entities.some(e => !hasPermissionFlag(flag, roles, e, project)) ? READONLY : {};
      const type = entities.every(e => e.type == entities[0].type) ? entities[0].type : undefined;

      return [
        { id: 'clone', title: 'Duplicate all', icon: faCopy, ...areEntitiesReadOnly(Permission.CanDuplicateEntities) },
        ...(type === EntityType.Drawing) ? [
          { id: 'download-png', title: 'Download all', icon: faCloudDownload },
        ] : [],
        DIVIDER,
        { id: 'bin', title: 'Move to bin all', icon: faTrash, ...areEntitiesReadOnly(Permission.CanDeleteEntities) },
        ...(type === EntityType.Drawing) ? [
          DIVIDER,
          { id: 'copy-link', title: 'Copy drawing links', icon: faLink, clickedTitle: 'Copied to clipboard' },
        ] : [],
      ];
    }
  }

  const isReadOnly = (flag: Permission, e: EntityData) => !hasPermissionFlag(flag, roles, e, project) ? READONLY : {};

  if (isBinFolder) {
    switch (entity.type) {
      case EntityType.Drawing:
        return [
          { id: 'restore', title: 'Restore', icon: trashArrowUpIcon, ...isReadOnly(Permission.CanDeleteEntities, entity) },
          { id: 'move', title: 'Move', icon: faFolderTree, ...isReadOnly(Permission.CanUpdateEntities, entity), ...isReadOnly(Permission.CanDeleteEntities, entity) },
          { id: 'delete', title: 'Delete', icon: faTrash, ...isReadOnly(Permission.CanDeleteEntities, entity), ...isReadOnly(Permission.CanDeleteEntitiesPermanently, entity), allowClick: true },
        ];
      case EntityType.Folder:
        return [
          { id: 'restore', title: 'Restore', icon: trashArrowUpIcon, ...isReadOnly(Permission.CanDeleteEntities, entity) },
          { id: 'move', title: 'Move', icon: faFolderTree, ...isReadOnly(Permission.CanUpdateEntities, entity), ...isReadOnly(Permission.CanDeleteEntities, entity) },
          { id: 'delete', title: 'Delete', icon: faTrash, ...isReadOnly(Permission.CanDeleteEntities, entity), ...isReadOnly(Permission.CanDeleteEntitiesPermanently, entity), allowClick: true },
        ];
      default:
        return [];
    }
  }

  switch (entity.type) {
    case EntityType.Flowchart:
      return [
        { id: 'share', title: 'Share', icon: faUserPlus },
        { id: 'embed', title: 'Embed', icon: faCode },
        { id: 'download', title: 'Download', icon: faCloudDownload },
        { id: 'move', title: 'Move', icon: faFolderTree, ...isReadOnly(Permission.CanUpdateEntities, entity) }, // check it
        { id: 'clone', title: 'Duplicate', icon: faCopy, ...isReadOnly(Permission.CanDuplicateEntities, entity) },
        { id: 'delete', title: 'Delete', icon: faTrash, ...isReadOnly(Permission.CanDeleteEntities, entity) },
      ];
    case EntityType.Drawing:
      return [
        { id: 'share', title: 'Share', icon: faUserPlus },
        DIVIDER,
        { id: 'rename', title: 'Rename', icon: faPen, ...isReadOnly(Permission.CanUpdateEntities, entity) },
        { id: 'clone', title: 'Duplicate', icon: faCopy, ...isReadOnly(Permission.CanDuplicateEntities, entity) },
        { id: 'move', title: 'Move', icon: faFolderTree, ...isReadOnly(Permission.CanUpdateEntities, entity) }, //check it
        DIVIDER,
        { id: 'download-png', title: 'Download', icon: faCloudDownload, ...isReadOnly(Permission.CanViewEntities, entity) },
        ...(disableSharingAsPng ? [] : [{ id: 'share-as-image', title: 'Share as PNG', icon: faShare, ...isReadOnly(Permission.CanExportEntityAsImage, entity) }]),
        { id: 'export', title: 'Export...', icon: faFileExport, ...isReadOnly(Permission.CanExportEntityAsImage, entity) },
        DIVIDER,
        { id: 'bin', title: 'Move to bin', icon: faTrash, ...isReadOnly(Permission.CanDeleteEntities, entity) },
        DIVIDER,
        { id: 'copy-link', title: 'Copy drawing link', icon: faLink, clickedTitle: 'Copied to clipboard' },
      ];
    case EntityType.Folder:
      return [
        { id: 'rename', title: 'Rename', icon: faPen, ...isReadOnly(Permission.CanUpdateEntities, entity) },
        { id: 'move', title: 'Move', icon: faFolderTree, ...isReadOnly(Permission.CanUpdateEntities, entity) },
        { id: 'clone', title: 'Duplicate', icon: faCopy, ...isReadOnly(Permission.CanDuplicateEntities, entity) },
        DIVIDER,
        { id: 'bin', title: 'Move to bin', icon: faTrash, ...isReadOnly(Permission.CanDeleteEntities, entity) },
      ];
    default:
      return [];
  }
}
